import * as React from 'react';

import {useGetActivitiesForWidget} from '../core-entity/widgets/activities-widget/graphql/hooks/use-get-activities-for-widget';

import {useGetLeadDripSequences} from './content/automated-emails-widget/graphql/hooks/use-get-lead-drip-sequences';
import {useGetLeadManuallyStartedEmailSequences} from './content/automated-emails-widget/graphql/hooks/use-get-lead-manually-started-email-sequences';
import {useGetLeadPersonalEmailSequences} from './content/automated-emails-widget/graphql/hooks/use-get-lead-personal-email-sequences';
import {useGetGoalsByStage} from './content/sales-process-widget/graphql/hooks/use-get-goals-by-stage';
import {useGetStepsByStage} from './content/sales-process-widget/graphql/hooks/use-get-steps-by-stage';
import {useGetLead} from './shared/graphql/hooks/use-get-lead';
import type {LeadUpdateEvent} from './types';

/* An event listener hook that refetches precise parts of a lead based on which
part of the lead was updated.
Only use this hook when an update affects OTHER parts of the app beyond
just the list of items being updated. If you only need to refetch that single query,
refetch that query directly instead of using this hook.
Use refetch-helpers.jsx to fire an event for this hook, because that way we
can properly type the events that are being fired to only ones that this hook
will listen for. */
export const useLeadUpdateRefetch = (leadId: string) => {
    const {refetch: refetchSteps} = useGetStepsByStage(leadId);
    const {refetch: refetchGoals} = useGetGoalsByStage(leadId, false);
    const {refetch: refetchLeadPersonalEmailSequences} = useGetLeadPersonalEmailSequences(leadId);
    const {refetch: refetchLeadDripSequences} = useGetLeadDripSequences(leadId);
    const {refetch: refetchLeadManuallyStartedEmailSequences} =
        useGetLeadManuallyStartedEmailSequences(leadId);
    const {refetch: refetchScheduledActivities} = useGetActivitiesForWidget(leadId, 0);
    const {refetch: refetchLoggedActivities} = useGetActivitiesForWidget(leadId, 1);
    const {refetch: refetchLead} = useGetLead(leadId, undefined, true);

    React.useEffect(() => {
        const refetchOnStageChange = () => {
            refetchSteps();
            refetchGoals();
            refetchLeadPersonalEmailSequences();
            refetchLeadDripSequences();
            refetchLead();
            Timeline.refreshTimeline();
        };

        const refetch = (e: CustomEvent) => {
            if (!e.detail || !e.detail.type) {
                return;
            }

            switch (e.detail.type as LeadUpdateEvent) {
                case 'stageChanged':
                    refetchOnStageChange();
                    break;
                case 'activityStatusChanged':
                    refetchScheduledActivities();
                    refetchLoggedActivities();
                    refetchOnStageChange();
                    break;
                case 'noteCreated':
                    refetchOnStageChange();
                    break;
                case 'emailSent':
                    refetchOnStageChange();
                    setTimeout(() => {
                        refetchLeadManuallyStartedEmailSequences();
                    }, 1000);
                    break;
                case 'manuallyStartedPersonalEmailSequenceCancel':
                    refetchLeadManuallyStartedEmailSequences();
                    break;
                case 'stepStatusChange':
                    refetchOnStageChange();
                    break;
                case 'dripSequenceCancel':
                    refetchLeadDripSequences();
                    break;
                case 'personalEmailSequenceStart':
                    refetchLeadPersonalEmailSequences();
                    break;
                case 'personalEmailSequenceSendNow':
                    setTimeout(() => {
                        refetchLeadPersonalEmailSequences();
                        refetchLeadManuallyStartedEmailSequences();
                    }, 1000);
                    Timeline.refreshTimeline();
                    break;
                case 'contactsUpdated':
                    refetchOnStageChange();
                    break;
                case 'accountsUpdated':
                    refetchLead();
                    refetchSteps();
                    break;
                case 'competitorsChanged':
                    refetchOnStageChange();
                    break;
                case 'customFieldUpdated':
                    refetchOnStageChange();
                    break;
                case 'salesDocumentStatusChange':
                    refetchOnStageChange();
                    break;
                case 'productsUpdated':
                    refetchOnStageChange();
                    break;
                case 'valueUpdated':
                    refetchOnStageChange();
                    break;
                case 'sourcesUpdated':
                    refetchOnStageChange();
                    break;
                case 'dueDateUpdated':
                    refetchOnStageChange();
                    break;
                default:
                    break;
            }
        };

        // @ts-expect-error
        window.addEventListener('leadUpdated', refetch);

        return () => {
            // @ts-expect-error
            window.removeEventListener('leadUpdated', refetch);
        };
    }, [
        leadId,
        refetchGoals,
        refetchLead,
        refetchLeadDripSequences,
        refetchLeadManuallyStartedEmailSequences,
        refetchLeadPersonalEmailSequences,
        refetchLoggedActivities,
        refetchScheduledActivities,
        refetchSteps,
    ]);
};
